import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useGetBoard } from '../../../../../queries';
import { useFinishImportOpportunitiesMutation } from '../../../../../queries/Boards/useFinishImportOpportunities';
import { useImportOpportunitiesMutation } from '../../../../../queries/Boards/useImportOpportunities';
import { baseStyleSheet } from '../../../../styles/styles';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../Breadcrumb';
import { MultiContainerHeader } from '../../../MultiContainerHeader';
import { MapStep } from '../../Steps/Map';
import { ProcessStep } from '../../Steps/Process';
import { UploadStep } from '../../Steps/Upload';
import { styleSheet } from './styles';

enum Step {
	Uploading,
	Mapping,
	Processing,
}

export const OpportunitiesImport = () => {
	const userSession = useUserSession();
	const { id: boardId } = useParams<{ id: string }>();
	const [file, setFile] = React.useState(null);
	const [systemJob, setSystemJob] = React.useState<Api.ISystemJob>(null);
	const [mappings, setMappings] = React.useState<Api.IDictionary<string>>({});
	const [mappingErrors, setMappingErrors] = React.useState<string[]>([]);
	const boardQuery = useGetBoard({
		boardId,
	});
	const importOpportunitiesMutation = useImportOpportunitiesMutation({
		onSuccess: (result: Api.IImportOpportunityPreview) => {
			setSystemJob({ id: result.id });
		},
	});
	const finishImportOpportunitiesMutation = useFinishImportOpportunitiesMutation({
		onSuccess: (newSystemJob: Api.ISystemJob) => {
			setSystemJob(newSystemJob);
		},
	});
	let currentStep: Step = Step.Uploading;
	if (importOpportunitiesMutation.status === 'success') {
		currentStep = Step.Mapping;
	}
	if (finishImportOpportunitiesMutation.status === 'success') {
		currentStep = Step.Processing;
	}

	const next = async () => {
		switch (currentStep) {
			case Step.Uploading:
				importOpportunitiesMutation.mutate({ file });
				break;
			case Step.Mapping:
				finishImportOpportunitiesMutation.mutate({
					systemJobId: systemJob.id,
					options: { fieldMapping: mappings, boardId },
				});
				break;
			default:
				throw new Error('Impossible scenario');
		}
	};

	const isUploading = currentStep === Step.Uploading;
	const isMapping = currentStep === Step.Mapping;
	const isProcessing = currentStep === Step.Processing;

	const canGoNext = React.useMemo(() => {
		if (isUploading) {
			return !!file;
		} else if (isMapping) {
			return mappingErrors.length === 0;
		} else {
			return false;
		}
	}, [isUploading, isMapping, file, mappingErrors]);

	const importError = finishImportOpportunitiesMutation.error?.systemMessage;

	const handleDownloadSample = React.useCallback(() => {
		userSession.webServiceHelper.callWebServiceRawResponse(
			'board/items/sampleCsv',
			'GET',
			null,
			response => {
				response.blob().then(blob => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = 'opportunities-sample.csv';
					document.body.appendChild(a);
					a.click();
					a.remove();
					window.URL.revokeObjectURL(url);
				});
			},
			error => {
				console.error('Failed to download sample CSV:', error);
			}
		);
	}, [userSession.webServiceHelper]);

	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/dataBoards'>Data Boards</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to={`/dataBoards/opportunities/${boardId}`}>
										{boardQuery.data?.name ?? 'Opportunities Board'}
									</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>Import Opportunities</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
			/>

			{isUploading ? (
				<UploadStep
					isLoading={importOpportunitiesMutation.isPending}
					file={file}
					setFile={setFile}
					onDownloadSample={handleDownloadSample}
					sampleDownloadLabel='Download a sample spreadsheet to see the expected format'
				/>
			) : null}

			{isMapping ? (
				<MapStep<Api.IImportOpportunityFieldConfig>
					isLoading={importOpportunitiesMutation.isPending}
					headers={importOpportunitiesMutation.data?.header}
					samples={importOpportunitiesMutation.data?.sample}
					fields={importOpportunitiesMutation.data?.fields}
					mappings={mappings}
					setMappings={setMappings}
					importError={importError}
					mappingErrors={mappingErrors}
					setMappingErrors={setMappingErrors}
				/>
			) : null}

			{isProcessing && !!systemJob ? (
				<ProcessStep
					systemJob={systemJob}
					setSystemJob={setSystemJob}
					singularEntity='opportunity'
					pluralEntity='opportunities'
				/>
			) : null}

			{!isProcessing ? (
				<div className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={next} disabled={!canGoNext}>
						<span>Next</span>
					</button>
				</div>
			) : null}
		</div>
	);
};
